import { PluginListenerHandle } from '@capacitor/core';

// для записи чистой epc
export const PASSWORD_FOR_WRITE_EPC = "12344321";

// Эти события генерирует плагин
export enum PLUGIN_EVENTS {
  // Событие нажатия кнопки на устройстве
  SCAN_KEY_EVENT = 'keyEvent',
  // Событие считывания RFID
  SCAN_RFID_EVENT = 'scanRfidEvent',
  // Событие изменения уровня заряда батареи
  BATTERY_LEVEL_CHANGE = 'batteryLevelChange',
  // Событие считывания штрих-кода
  SCAN_BARCODE_EVENT = 'scanBarcode',
  // Событие считывания epc при поиске
  SEARCH_UHF_EVENT = "SEARCH_UHF_EVENT",
}

// эти события генерирует хук и отправляет их в приложение
export enum EVENTS {
  SCAN_KEY_EVENT = 'keyEvent',
  SCAN_RFID_EVENT = 'rfidEvent',
  BATTERY_LEVEL_CHANGE = 'batteryLevelEvent',
  SCAN_BARCODE_EVENT = 'barcodeEvent',
  RFID_POWER_CHANGE = 'rfidPowerChange',
  SEARCH_UHF_EVENT = "SEARCH_UHF_EVENT",
}

// Событие нажатия кнопки на устройстве
export type ScanKeyEvent = {
  code: number;
  action: 'down' | 'up';
}

// Событие считывания RFID
export type ScanRfidEvent = {
  // значение RFID
  value: string;
  // мощность, на которой был считан RFID
  power?: string;
}

// Событие поиска UHF
export type SearchUHFEvent = {
  // значение дальности
  value: number;
}

export interface RfidScanInfo {
  rfidData: string;
}

export interface ScanInfo {
  barcode: string;
}

export interface BatteryInfo {
  batteryLevel: number;
}

export interface RfidInfo {
  rfidPower?: number;
  rfidVersion?: string;
  rfidHardwareVersion?: string;
}

export interface DeviceInfo extends Pick<BatteryInfo, 'batteryLevel'>, RfidInfo {
  id: string;
  brand: string;
  model: string;
  apkVersion: string;
  isBarcodeScanner: boolean;
  isRfidReader: boolean;
  isSupported: boolean;
  isInventorySingle: boolean;
  barcodeKeyCode: number;
  rfidGunKeyCode: number;
}

interface WriteRfidDataOptions {
  epc: string;
  password?: string;
  bank?: number;
  ptr?: number;
  filterData?: string;
  filterBank?: number;
  filterPtr?: number;
}

export enum LOCK_MEM_TYPE {
  ALL = 'ALL',
  EPC = 'EPC',
  KILL = 'KILL',
  ACCESS = 'ACCESS',
}

export interface ShopflowPlugin {
  /**
   * методы для работы с батареей
   * addListener - подписка на события 'PLUGIN_EVENTS.BATTERY_LEVEL_CHANGE' от устройства
   * startBatteryTracking - запуск отслеживания уровня заряда батареи
   * stopBatteryTracking - остановка отслеживания уровня заряда батареи
   */
  addListener(eventName: PLUGIN_EVENTS.BATTERY_LEVEL_CHANGE, listenerFunc: (event: BatteryInfo) => void): Promise<PluginListenerHandle>;
  startBatteryTracking(): Promise<void>;
  stopBatteryTracking(): Promise<void>;
  /**
   * методы для работы со сканером штрих-кодов
   * addListener - подписка на события 'PLUGIN_EVENTS.SCAN_BARCODE_EVENT' от устройства
   * startBarcodeTracking - запуск отслеживания штрих-кода
   * stopBarcodeTracking - остановка отслеживания штрих-кода
   */
  addListener(eventName: PLUGIN_EVENTS.SCAN_BARCODE_EVENT, listenerFunc: (event: ScanInfo) => void): Promise<PluginListenerHandle>;
  startBarcodeTracking(): Promise<void>;
  stopBarcodeTracking(): Promise<void>;
  /**
   * методы для работы с RFID
   * addListener - подписка на события 'PLUGIN_EVENTS.SCAN_RFID_EVENT' от устройства
   * onceReadRfid - единичное считывание RFID
   * onceReadBankEPC - единичное считывание данных из банка памяти EPC
   * startMultiReadRfid - запуск многократного считывания RFID
   * stopMultiReadRfid - остановка многократного считывания RFID
   * writeRfidData - запись данных в RFID
   * writeRfidPassword - запись пароля в RFID
   * lockMem - блокировка памяти RFID
   */
  addListener(eventName: PLUGIN_EVENTS.SCAN_RFID_EVENT, listenerFunc: (event: ScanRfidEvent) => void): Promise<PluginListenerHandle>;
  onceReadRfid(): Promise<{ value: string, power: string; }>;
  onceReadBankEPC(): Promise<{ value: string }>;
  startMultiReadRfid(): Promise<void>;
  stopMultiReadRfid(): Promise<void>;
  writeRfidData(options: WriteRfidDataOptions): Promise<{ isEpcWritten: boolean }>;
  writeRfidPassword(options: { password?: string, newPassword?: string; filterData?: string; }): Promise<{ isPasswordWritten: boolean }>;
  lockMem(options: { password: string; filterData?: string; type: LOCK_MEM_TYPE }): Promise<{ isLocked: boolean }>;

  /**
   * методы для получения информации о устройстве
   * getDeviceInfo - получение информации о устройстве
   */
  getDeviceInfo(): Promise<DeviceInfo>;

  /**
   * методы для работы с мощностью RFID
   * setRfidPower - установка мощности RFID
   * getRfidPower - получение мощности RFID
   */
  setRfidPower(options: { power: number }): Promise<{ isPowerSetted: boolean }>;
  getRfidPower(): Promise<{ power: number }>;
  
  /**
   * методы для работы со звуком
   * playSound - проигрывание звука
   * stopSoundLoop - остановка звука
   */
  playSound(options: { soundId: number, isLoop: boolean }): Promise<void>;
  stopSoundLoop(): Promise<void>;
  /**
   * метод для сохранения клавиш, по которым будут отправляться события
   * @param options.keys - массив кодов клавиш
   * @returns {success: boolean} - результат сохранения
   */
  setEventKeys(options: { keys: number[] }): Promise<{ success: boolean }>;

  // TODO
  setPowerWrite(options: { power: number }): Promise<{ success: boolean }>;

  // ------новая жизнь
  startKeyEventListener(): Promise<void>;
  stopKeyEventListener(): Promise<void>;
  addListener(eventName: PLUGIN_EVENTS.SCAN_KEY_EVENT, listenerFunc: (event: ScanKeyEvent) => void): Promise<PluginListenerHandle>;

  /**
   * Запуск поиска uhf(epc)
   * @param filter - параметр для фильтрации
   */
  startSearchUHF(options: { filter: string }): Promise<{ success: boolean }>;
  /**
   * Остановка поиска uhf(epc)
   */
  stopSearchUHF(): Promise<{ success: boolean }>;
  /**
   * Добавить слушатель события SEARCH_UHF_EVENT
   * @param eventName - имя события SEARCH_UHF_EVENT
   * @param listenerFunc - callback, который будет вызываться
   */
  addListener(eventName: PLUGIN_EVENTS.SEARCH_UHF_EVENT, listenerFunc: (event: { value: number }) => void): Promise<PluginListenerHandle>;
}
